import FaceTecSDK, {
  FaceTecInitializationError,
  type FaceTecSDKInstance,
  type FaceTecSessionRequestProcessor,
} from "facetec-sdk";
import FaceTecInitError from "./logic/FaceTecInitError";
import { createSessionTracker, trackInstanceSessions } from "./sessionTracker";

export type FaceTecInitParams = {
  deviceKeyIdentifier: string;
  sessionRequestProcessor: FaceTecSessionRequestProcessor;
};

// The instance being initialized, along with the fn to deinit it.
export type FaceTecInitResult = [
  instance: Promise<FaceTecSDKInstance>,
  deinit: () => Promise<void>,
];

const withTrace = <T>(
  promise: Promise<T>,
  name: string,
  info: unknown[],
): Promise<T> => {
  const rand = Math.random();

  console.log(`[${rand}] Calling ${name} with `, ...info);

  return promise.then(
    (val) => {
      console.log(`[${rand}] Resolved ${name} with `, val);
      return val;
    },
    (err) => {
      console.log(`[${rand}] Rejected ${name} with `, err);
      throw err;
    },
  );
};

const withSession = (
  params: FaceTecInitParams,
): Promise<FaceTecSDKInstance> => {
  const { deviceKeyIdentifier, sessionRequestProcessor } = params;

  return withTrace(
    new Promise((resolve, reject) => {
      const onError = (error: FaceTecInitializationError) =>
        reject(new FaceTecInitError(error));

      FaceTecSDK.initializeWithSessionRequest(
        deviceKeyIdentifier,
        sessionRequestProcessor,
        { onError, onSuccess: resolve },
      );
    }),
    "FaceTecSDK.initializeWithSessionRequest",
    [deviceKeyIdentifier, sessionRequestProcessor],
  );
};

// The SDK skips the deinit while a session is running, and the promise then
// never settles: call it only once the sessions have exited.
const deinitialize = (): Promise<void> =>
  withTrace(
    new Promise((resolve) => FaceTecSDK.deinitialize(resolve)),
    "FaceTecSDK.deinitialize",
    [],
  );

// Inits the SDK, and returns the instance along with the fn to deinit it.
//
// The deinit waits for the sessions started on the instance to exit: the SDK
// clears its busy state before reporting the exit, so the deinit is not
// skipped.
export const initialize = (params: FaceTecInitParams): FaceTecInitResult => {
  const sessions = createSessionTracker();

  const instance = withSession(params).then((instance) =>
    trackInstanceSessions(instance, sessions),
  );

  const deinit = async () => {
    await sessions.idle();
    await deinitialize();
  };

  return [instance, deinit];
};
